import { Fragment } from 'react';
import { Link } from 'react-router';
import { CheckCircle2, CircleDot, XCircle, HelpCircle } from 'lucide-react';
import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner } from '../components/Section';
import { ContactCta } from '../components/ContactCta';
import type { FrameworkReport, ControlEvidence, ControlFamily, ControlRelatedModule } from '../types';

// Related-module -> results route (vulns/exposure share /vuln; techstack /tech).
function moduleToPath(mod?: ControlRelatedModule): string | null {
  switch (mod) {
    case 'dns':        return '../dns';
    case 'email':      return '../email';
    case 'web':        return '../web';
    case 'ssl':        return '../ssl';
    case 'whois':      return '../whois';
    case 'subdomains': return '../subdomains';
    case 'infra':      return '../infra';
    case 'techstack':  return '../tech';
    case 'vulns':
    case 'exposure':   return '../vuln';
    default:           return null;
  }
}

export function CompliancePage() {
  const scan = useScan();
  const c = scan.compliance;

  return (
    <>
      <PageHeader
        title="Compliance Posture"
        subtitle="How this scan's observations map to common frameworks. Note: external configuration is only one input — formal compliance also requires evidence from internal controls (logs, access reviews, vendor management) we can't observe."
      />

      <Section accent="blue">
        <Banner tone="info" title="What this page shows vs the Executive Dashboard">
          The Executive Dashboard answers <em>"what should I do this week?"</em>{' '}
          The Compliance page answers <em>"where do I stand against framework X?"</em>{' '}
          Same data, different angles — use the framework chips on Executive's
          priority fixes to jump back here.
        </Banner>
      </Section>

      {/* Framework cards (CMMC leads; its CTA sits right beneath it) */}
      {c.frameworks.map(f => (
        <Fragment key={f.id}>
          <FrameworkCard framework={f} />
          {f.id === 'cmmc' && (
            <Section title="Pursuing CMMC Level 2?" accent="blue">
              <p className="text-sm leading-relaxed text-ink-2">
                This scan covers the externally-observable slice of CMMC Level 2,
                roughly 6 of the 110 NIST SP 800-171 practices. The rest (access
                control, audit logging, MFA, training, physical, and incident
                response) require a full assessment. PROTBYTE takes defense
                suppliers from gap assessment to Level 2 readiness.
              </p>
              <div className="mt-4">
                <ContactCta label="Book a CMMC gap assessment" />
              </div>
            </Section>
          )}
        </Fragment>
      ))}

      {/* Technical RFC checklist — engineer-facing detail layer */}
      <RfcChecklist />
    </>
  );
}

// ---------------------------------------------------------------------------
// Framework card — high level posture for one framework
// ---------------------------------------------------------------------------
function FrameworkCard({ framework }: { framework: FrameworkReport }) {
  // Exclude `unknown` (couldn't-assess) controls from the denominator, else a
  // fully-passing framework with a few unassessable controls reads as a failure.
  const assessable = framework.satisfied + framework.partial + framework.gap;
  const score = assessable > 0
    ? Math.round((framework.satisfied / assessable) * 100)
    : 0;
  const accent: 'green' | 'amber' | 'rose' =
    score >= 80 ? 'green' : score >= 50 ? 'amber' : 'rose';
  const barColor =
    score >= 80 ? 'bg-emerald-500' :
    score >= 50 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <Section
      title={framework.name}
      accent={accent}
      status={{
        label: `${framework.satisfied}/${framework.total} satisfied`,
        tone: score >= 80 ? 'ok' : score >= 50 ? 'warn' : 'bad'
      }}
    >
      <p className="text-xs text-ink-2">{framework.description}</p>

      <div className="mt-4 flex items-center gap-3">
        <span className="tnum text-2xl font-bold text-ink">{score}%</span>
        <div className="flex-1">
          <div className="h-2.5 rounded-full bg-slate-200">
            <div className={`h-2.5 rounded-full ${barColor}`} style={{ width: `${score}%` }} />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-ink-3">
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" strokeWidth={2.5} aria-hidden="true" />
              {framework.satisfied} satisfied
            </span>
            <span className="inline-flex items-center gap-1">
              <CircleDot className="h-3 w-3 text-amber-600" strokeWidth={2.5} aria-hidden="true" />
              {framework.partial} partial
            </span>
            <span className="inline-flex items-center gap-1">
              <XCircle className="h-3 w-3 text-rose-600" strokeWidth={2.5} aria-hidden="true" />
              {framework.gap} gap
            </span>
            {framework.unknown > 0 && (
              <span className="inline-flex items-center gap-1">
                <HelpCircle className="h-3 w-3 text-ink-3" strokeWidth={2.5} aria-hidden="true" />
                {framework.unknown} unknown
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {framework.families.map(fam => <FamilyBlock key={fam.name} family={fam} />)}
      </div>
    </Section>
  );
}

function FamilyBlock({ family }: { family: ControlFamily }) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-3">
        {family.name}
      </p>
      <ul className="space-y-1.5">
        {family.controls.map(c => <ControlRow key={c.control_id} control={c} />)}
      </ul>
    </div>
  );
}

function ControlRow({ control }: { control: ControlEvidence }) {
  const StatusIcon = {
    satisfied: CheckCircle2,
    partial:   CircleDot,
    gap:       XCircle,
    unknown:   HelpCircle
  }[control.status];

  const statusColor = {
    satisfied: 'text-emerald-600',
    partial:   'text-amber-600',
    gap:       'text-rose-600',
    unknown:   'text-ink-3'
  }[control.status];

  const statusBg = {
    satisfied: 'tint-ok',
    partial:   'tint-warn',
    gap:       'tint-bad',
    unknown:   'bg-panel-2 border-line'
  }[control.status];

  return (
    <li className={`rounded-md border px-3 py-2.5 text-sm ${statusBg}`}>
      <div className="flex items-start gap-2">
        <StatusIcon
          className={`mt-0.5 h-4 w-4 shrink-0 ${statusColor}`}
          strokeWidth={2.25}
          aria-label={control.status}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-mono text-[11px] font-semibold text-ink-2">
              {control.control_id}
            </span>
            <span className="text-ink">{control.control_name}</span>
            {moduleToPath(control.related_module) && (
              <Link to={moduleToPath(control.related_module)!}
                title={`Open ${control.related_module} details`}
                className="text-[10px] font-medium uppercase tracking-wide text-accent hover:text-sentinel-900">
                {control.related_module} →
              </Link>
            )}
          </div>
          {control.evidence.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs text-ink-2">
              {control.evidence.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Technical RFC checklist — engineer-facing layer beneath the framework view
// ---------------------------------------------------------------------------
function RfcChecklist() {
  const scan = useScan();
  const m = scan.modules;

  interface Check { name: string; rfc: string; passed: boolean }
  const checks: Check[] = [
    { name: 'SPF (Sender Policy Framework)',     rfc: 'RFC 7208',           passed: m.email?.spf.rfc7208_compliant ?? false },
    { name: 'DKIM (DomainKeys Identified Mail)', rfc: 'RFC 6376',           passed: m.email?.dkim.configured ?? false },
    { name: 'DMARC',                              rfc: 'RFC 7489',           passed: m.email?.dmarc.configured ?? false },
    { name: 'MTA-STS',                            rfc: 'RFC 8461',           passed: m.email?.mta_sts.configured ?? false },
    { name: 'TLS Reporting (TLS-RPT)',            rfc: 'RFC 8460',           passed: m.email?.tls_rpt.configured ?? false },
    { name: 'BIMI Brand Indicators',              rfc: 'Draft',              passed: m.email?.bimi.configured ?? false },
    { name: 'DNSSEC',                             rfc: 'RFC 4033/4034/4035', passed: m.dns?.dnssec.enabled ?? false },
    { name: 'CAA Records',                        rfc: 'RFC 8659',           passed: (m.dns?.records.caa.length ?? 0) > 0 },
    { name: 'HSTS Strict-Transport-Security',     rfc: 'RFC 6797',           passed: !!m.web?.headers.hsts },
    { name: 'Content Security Policy',            rfc: 'CSP Level 3',        passed: !!m.web?.headers.csp }
  ];

  return (
    <Section title="Underlying Standards Adoption (RFCs)" accent="slate">
      <p className="text-sm text-ink-2">
        Engineer-facing detail. Each line tracks adoption of an individual
        technical standard — these are the building blocks the framework
        controls above depend on.
      </p>
      <ul className="mt-4 divide-y divide-line overflow-hidden rounded-lg border border-line">
        {checks.map((c, i) => (
          <li key={i} className="flex items-center justify-between gap-4 bg-panel px-4 py-2.5 text-sm hover:bg-panel-2">
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{c.name}</p>
              <p className="text-xs text-ink-3">{c.rfc}</p>
            </div>
            <span className={`pill ${c.passed ? 'pill-ok' : 'pill-warn'}`}>
              {c.passed ? 'IMPLEMENTED' : 'MISSING'}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
